import { useMemo, useState } from 'react';
import useFetch from 'react-fetch-hook';
import { FaArrowDown, FaArrowUp } from 'react-icons/fa';
import { Norway } from './maps/Sweden';
import { Table } from './Table';

/** Information about a region of a country. */
export type Region = {
  code: string;
  name: string;
  color: string;
};

/** Structure of data sent into the table. */
export type TableData = {
  color: string;
  region: string;
  price: number;
  previousPrices: number[];
  change: number;
};

/** Map regions from code to name and color. */
const regions: Region[] = [
  { code: 'SE1', name: 'SE 1 - Luleå', color: 'blue' },
  { code: 'SE2', name: 'SE 2 - Sundsvall', color: 'purple' },
  { code: 'SE3', name: 'SE 3 - Stockholm', color: 'yellow' },
  { code: 'SE4', name: 'SE 4 - Malmö', color: 'red' },
];

/** App to display an overview of spot prices in the electricity market. */
export const App = () => {
  const { isLoading, data } = useFetch<any>('https://leazardcloud.com/production/spotprices?country=SE');

  const tableData = useMemo<TableData[]>(() => {
    return data
      ? regions.map(({ code, color, name }) => {
          const regionData = data.data[code];
          return {
            color,
            region: name,
            price: regionData[regionData.length - 1].value,
            previousPrices: [1],
            change: (regionData[regionData.length - 1].value - regionData[0].value) / regionData[0].value,
          };
        })
      : [];
  }, [data]);

  const [activeRegion, setActiveRegion] = useState<string | null>(null);

  return isLoading ? (
    <span className="text-base">Laddar...</span>
  ) : (
    <div className="md:flex md:justify-center md:items-center md:gap-12">
      <div className="hidden md:block md:w-6/12 lg:w-4/12 xl:w-3/12">
        <Norway onHover={setActiveRegion} onBlur={() => setActiveRegion(null)} activeRegion={activeRegion} />
      </div>
      <div>
        <Table
          activeRow={(row) => !activeRegion || row.region === activeRegion}
          columns={['color', 'region', 'price', 'change']}
          columnLabels={['', 'Elområde', 'Spotpris', 'Förändring senaste dygnet']}
          columnClasses={['hidden md:flex']}
          data={tableData}
          onHover={({ region }) => setActiveRegion(region)}
          onBlur={() => setActiveRegion(null)}
        >
          {(column, { color, region, price, change }) => {
            switch (column) {
              case 'color':
                return <div className={`w-6 h-6 bg-${color}-500 rounded-full`}></div>;
              case 'region':
                return <div className="text-left">{region}</div>;
              case 'price':
                return (
                  <div className="flex gap-2">
                    <span className="font-semibold text-sm md:text-base">{Math.ceil(price)}</span>
                    <span className="font-normal text-gray-500 text-sm md:text-base">öre/kWh</span>
                  </div>
                );
              case 'change':
                return (
                  <div className="flex items-center gap-2">
                    {change > 0 ? (
                      <FaArrowUp className="fill-current text-red-500" />
                    ) : (
                      <FaArrowDown className="fill-current text-green-500" />
                    )}
                    <span className="font-semibold text-sm md:text-base">{(change * 100).toFixed(1)} </span>
                    <span className="font-normal text-gray-500 text-sm md:text-base"> %</span>
                  </div>
                );
            }
          }}
        </Table>
        <div className="pt-4 text-xs text-gray-400">
          Priserna uppdateras varje timme och kommer från den nordiska elbörsen Nord Pool. För konsumenten tillkommer
          utöver detta energiskatt, moms, kostnader för elcertifikat och elhandelsbolagets påslag.
        </div>
      </div>
    </div>
  );
};
